/**
 * 钉钉人员同步范围：确认文案与同步中心展示（父节点折叠）。最后更新：2026-08-30
 */

/** 部门 ID 统一转字符串，避免雪花 ID 精度丢失 */
export function normalizeDeptId(value) {
  if (value === undefined || value === null || value === '') return ''
  return String(value).trim()
}

function getNodeOrgType(node) {
  return String((node && (node.DeptOrgType || node.deptOrgType)) || '').toLowerCase()
}

/**
 * 组织树扁平化（教学/内部树节点结构）。
 * @param {Object|Array} tree
 * @returns {Array<{ id: string, pid: string, name: string, deptOrgType: string }>}
 */
export function flattenDepartmentTree(tree) {
  const list = []
  const walk = (node) => {
    if (!node || typeof node !== 'object') return
    const id = normalizeDeptId(node.value != null ? node.value : node.Id)
    if (!id) return
    const rawName = node.label || node.Name || node.name
    list.push({
      id,
      pid: normalizeDeptId(node.Pid != null ? node.Pid : node.pid),
      name: rawName || id,
      deptOrgType: getNodeOrgType(node),
    })
    ;(node.children || []).forEach(walk)
  }
  if (Array.isArray(tree)) tree.forEach(walk)
  else walk(tree)
  return list
}

/**
 * 合并额外 id->name 映射（组织树未命中时补全）。
 * @param {Array} flatDepartments
 * @param {Object<string, string>} nameById
 * @returns {Array}
 */
export function mergeDepartmentNameMap(flatDepartments, nameById = {}) {
  const byId = new Map()
  ;(flatDepartments || []).forEach((dept) => {
    if (!dept || !dept.id) return
    byId.set(normalizeDeptId(dept.id), { ...dept, id: normalizeDeptId(dept.id) })
  })
  Object.keys(nameById || {}).forEach((id) => {
    const normalizedId = normalizeDeptId(id)
    const name = nameById[id]
    if (!normalizedId || !name) return
    if (byId.has(normalizedId)) {
      byId.get(normalizedId).name = name
    } else {
      byId.set(normalizedId, { id: normalizedId, pid: '', name, deptOrgType: '' })
    }
  })
  return [...byId.values()]
}

function buildDeptIndex(flatDepartments) {
  const byId = new Map()
  const childrenByPid = new Map()
  ;(flatDepartments || []).forEach((dept) => {
    if (!dept || !dept.id) return
    const id = normalizeDeptId(dept.id)
    byId.set(id, dept)
    const pid = normalizeDeptId(dept.pid)
    if (!childrenByPid.has(pid)) childrenByPid.set(pid, [])
    childrenByPid.get(pid).push(id)
  })
  return { byId, childrenByPid }
}

function hasSelectedDescendant(id, selected, childrenByPid) {
  if (selected.has(id)) return true
  return (childrenByPid.get(id) || []).some((childId) => hasSelectedDescendant(childId, selected, childrenByPid))
}

/** 所选节点及其子孙是否全部被勾选覆盖 */
function isFullyCovered(id, selected, childrenByPid) {
  if (selected.has(id)) return true
  const children = childrenByPid.get(id) || []
  if (!children.length) return false
  return children.every((childId) => isFullyCovered(childId, selected, childrenByPid))
}

function collectDisplayRootIds(rootId, selected, childrenByPid) {
  const results = []
  const walk = (id) => {
    if (!hasSelectedDescendant(id, selected, childrenByPid)) return
    if (isFullyCovered(id, selected, childrenByPid)) {
      results.push(id)
      return
    }
    ;(childrenByPid.get(id) || []).forEach(walk)
  }
  walk(rootId)
  return results
}

function minimalSelectedRootIds(selected, byId) {
  return [...selected].filter((id) => {
    let pid = byId.get(id) && byId.get(id).pid
    while (pid) {
      if (selected.has(pid)) return false
      pid = byId.get(pid) && byId.get(pid).pid
    }
    return true
  })
}

function resolveSchoolRootId(flatDepartments, rootId) {
  const normalizedRootId = normalizeDeptId(rootId)
  if (normalizedRootId) return normalizedRootId
  const school = (flatDepartments || []).find((d) => d.deptOrgType === 'school')
  return school ? normalizeDeptId(school.id) : ''
}

function resolveDeptName(byId, id, nameById = {}) {
  const normalizedId = normalizeDeptId(id)
  const dept = byId.get(normalizedId)
  if (dept && dept.name && dept.name !== normalizedId) return dept.name
  if (nameById[normalizedId]) return nameById[normalizedId]
  return ''
}

/**
 * 将勾选的部门 ID 折叠为最少父节点名称列表。
 * @param {Array<string|number>} selectedIds
 * @param {Array} flatDepartments flattenDepartmentTree 结果
 * @param {{ rootId?: string|number, schoolScope?: boolean, nameById?: Object }} [options]
 * @returns {string[]}
 */
export function summarizeSyncScopeLabels(selectedIds, flatDepartments, options = {}) {
  if (options.schoolScope) return ['全校']
  const nameById = options.nameById || {}
  const selected = new Set((selectedIds || [])
    .map((id) => normalizeDeptId(id))
    .filter(Boolean))
  if (!selected.size) return []

  const mergedFlat = mergeDepartmentNameMap(flatDepartments, nameById)
  const { byId, childrenByPid } = buildDeptIndex(mergedFlat)

  if (selected.size === 1) {
    const id = [...selected][0]
    const name = resolveDeptName(byId, id, nameById)
    return name ? [name] : []
  }

  const rootId = resolveSchoolRootId(mergedFlat, options.rootId)
  if (rootId && isFullyCovered(rootId, selected, childrenByPid)) {
    const name = resolveDeptName(byId, rootId, nameById)
    return name ? [name] : []
  }

  let rootIds = rootId ? collectDisplayRootIds(rootId, selected, childrenByPid, byId) : []
  if (!rootIds.length) {
    rootIds = minimalSelectedRootIds(selected, byId)
  }

  return rootIds
    .map((id) => resolveDeptName(byId, id, nameById))
    .filter(Boolean)
}

/**
 * 范围名称列表转界面展示文案。
 * @param {string[]} labels
 * @returns {string}
 */
export function formatSyncScopeDisplayText(labels) {
  if (!labels || !labels.length) return '-'
  if (labels.length === 1) return labels[0]
  if (labels.length <= 3) return labels.join('、')
  return `${labels.slice(0, 2).join('、')}等${labels.length}个范围`
}

/**
 * 从任务记录解析 ScopeDepartmentIds。
 * @param {Object} task
 * @returns {string[]}
 */
export function parseTaskScopeDepartmentIds(task) {
  const raw = task && (task.ScopeDepartmentIds || task.scopeDepartmentIds || '')
  return String(raw)
    .split(',')
    .map((value) => normalizeDeptId(value))
    .filter(Boolean)
}

/**
 * 从任务记录解析范围展示文案。
 * @param {Object} task
 * @param {Object<number, Array>} deptFlatByBigType { 0: flat[], 1: flat[] }
 * @param {Object<string, string>} [nameById]
 * @returns {string}
 */
export function formatTaskScopeText(task, deptFlatByBigType = {}, nameById = {}) {
  if (!task) return '-'
  const scopeType = String(task.ScopeType || task.scopeType || '').toLowerCase()
  if (scopeType === 'school') return '全校'
  if (scopeType === 'selected') return '勾选导入'

  const selectedIds = parseTaskScopeDepartmentIds(task)
  const bigType = Number(task.BigType != null ? task.BigType : task.bigType)
  const flat = deptFlatByBigType[bigType] || deptFlatByBigType[1] || deptFlatByBigType[0] || []
  const rootId = task.SchoolDepartmentId || task.schoolDepartmentId || 0
  const labels = summarizeSyncScopeLabels(selectedIds, flat, { rootId, nameById })
  return formatSyncScopeDisplayText(labels)
}

/**
 * 收集任务列表中需补全名称的部门 ID。
 * @param {Array} rows
 * @param {Object<number, Array>} deptFlatByBigType
 * @param {Object<string, string>} nameById
 * @returns {string[]}
 */
export function collectMissingScopeDepartmentIds(rows, deptFlatByBigType = {}, nameById = {}) {
  const missing = new Set()
  ;(rows || []).forEach((row) => {
    const scopeType = String(row.ScopeType || row.scopeType || '').toLowerCase()
    if (scopeType === 'school' || scopeType === 'selected') return
    const bigType = Number(row.BigType != null ? row.BigType : row.bigType)
    const flat = deptFlatByBigType[bigType] || deptFlatByBigType[1] || deptFlatByBigType[0] || []
    const mergedFlat = mergeDepartmentNameMap(flat, nameById)
    const { byId } = buildDeptIndex(mergedFlat)
    parseTaskScopeDepartmentIds(row).forEach((id) => {
      const name = resolveDeptName(byId, id, nameById)
      if (!name) missing.add(id)
    })
  })
  return [...missing]
}

/**
 * 统计勾选范围下会被同步的班级数量（含勾选父节点下的全部班级）。
 * @param {Array} checkedNodes el-tree getCheckedNodes 结果
 * @returns {number}
 */
export function countSyncScopeClasses(checkedNodes) {
  const classIds = new Set()
  const walk = (node) => {
    if (!node) return
    const id = normalizeDeptId(node.value)
    if (getNodeOrgType(node) === 'class' && id) {
      classIds.add(id)
    }
    ;(node.children || []).forEach(walk)
  }
  ;(checkedNodes || []).forEach(walk)
  return classIds.size
}

/**
 * 从勾选节点提取部门 ID。
 * @param {Array} checkedNodes
 * @returns {string[]}
 */
export function extractScopeIdsFromCheckedNodes(checkedNodes) {
  return (checkedNodes || [])
    .map((node) => normalizeDeptId(node.value))
    .filter(Boolean)
}

/**
 * @param {{ scopeType: string, scopeDepartmentIds?: Array<string|number> }} scopePayload
 * @param {string} resourceLabel 如「学生」「教师」
 * @param {{ resourceType?: string, checkedNodes?: Array, countMode?: 'class'|'department', flatDepartments?: Array, rootId?: string|number, nameById?: Object }} [options]
 * @returns {string}
 */
export function buildSyncScopeConfirmMessage(scopePayload, resourceLabel, options = {}) {
  const label = resourceLabel || '人员'
  const checkedNodes = options.checkedNodes || []
  const flatDepartments = options.flatDepartments || []

  if (!scopePayload) {
    return `确认提交${label}同步任务吗？`
  }
  if (scopePayload.scopeType === 'School') {
    return `确认同步全校${label}数据吗？`
  }

  const scopeIds = Array.isArray(scopePayload.scopeDepartmentIds)
    ? scopePayload.scopeDepartmentIds.map((id) => normalizeDeptId(id)).filter(Boolean)
    : extractScopeIdsFromCheckedNodes(checkedNodes)

  if (scopeIds.length) {
    const names = summarizeSyncScopeLabels(scopeIds, flatDepartments, {
      rootId: options.rootId,
      nameById: options.nameById,
    })
    if (names.length === 1) {
      return `确认同步「${names[0]}」的${label}数据吗？`
    }
    if (names.length > 1) {
      return `确认同步「${formatSyncScopeDisplayText(names)}」的${label}数据吗？`
    }
  }

  if (checkedNodes.length) {
    const nodeNames = checkedNodes
      .map((node) => node.label || node.Name || node.name)
      .filter(Boolean)
    if (nodeNames.length === 1) {
      return `确认同步「${nodeNames[0]}」的${label}数据吗？`
    }
    if (nodeNames.length > 1) {
      return `确认同步「${formatSyncScopeDisplayText(nodeNames)}」的${label}数据吗？`
    }
  }

  const countMode = options.countMode || 'department'
  const useClassCount = countMode === 'class'
  const count = useClassCount
    ? countSyncScopeClasses(checkedNodes)
    : scopeIds.length

  if (count > 0) {
    const unit = useClassCount ? '班级' : '部门'
    return `确认同步所选 ${count} 个${unit}的${label}数据吗？`
  }
  return `确认提交${label}同步任务吗？`
}
